import { Box, useToast } from '@chakra-ui/react';
import { skipToken } from '@tanstack/react-query';
import { useStep } from '@duke-hq/ui/components/StepWizard/useStep';
import { SortOrder } from '@duke-hq/lib/pagination';
import { t } from '@lingui/core/macro';
import { PlusIcon } from 'lucide-react';
import { SelectReplacementCourse } from './SelectReplacementCourse';
import { ReviewSupersededSettings } from './ReviewSupersededSettings';
import { SupersessionSummary } from './SupersessionSummary';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useState, useCallback, useMemo } from 'react';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { djs } from '@duke-hq/djs';

import type { PaginatedCourse, Course, SupersedeFormData } from './types';

function CourseMarkSupersededSteps({
  currentStep,
  goToNextStep,
  goToPrevStep,
  currentCourse,
  replacementCourse,
  setReplacementCourse,
  setFormData,
  allCourses,
  handleSubmit,
  isSubmitting,
  initialFormData,
  title,
  isEffectiveDatePassed,
  isTeachOutDatePassed,
}: {
  currentStep: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  currentCourse: PaginatedCourse;
  replacementCourse: Course | undefined;
  setReplacementCourse: (course: Course) => void;
  setFormData: (data: SupersedeFormData) => void;
  allCourses: Course[];
  handleSubmit: () => void;
  isSubmitting: boolean;
  initialFormData: SupersedeFormData | undefined;
  title: string;
  isEffectiveDatePassed: boolean;
  isTeachOutDatePassed: boolean;
}) {
  if (currentCourse == null) {
    return null;
  }

  if (currentStep === 0) {
    return (
      <SelectReplacementCourse
        currentCourse={currentCourse}
        allCourses={allCourses}
        goToNextStep={goToNextStep}
        setReplacementCourse={setReplacementCourse}
        setFormData={setFormData}
        initialFormData={initialFormData}
        title={title}
        isEffectiveDatePassed={isEffectiveDatePassed}
        isTeachOutDatePassed={isTeachOutDatePassed}
      />
    );
  }

  if (currentStep === 1 && replacementCourse) {
    return (
      <ReviewSupersededSettings
        currentCourse={currentCourse}
        replacementCourse={replacementCourse}
        goToPrevStep={goToPrevStep}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        title={title}
      />
    );
  }

  return null;
}

function getSupersessionModalTitle(status: PaginatedCourse['status']): string {
  if (status === CourseStatusSchema.enum.SUPERSEDED) {
    return t`Supersession details`;
  }
  if (status === CourseStatusSchema.enum.PENDING_SUPERSESSION) {
    return t`Edit supersession`;
  }
  return t`Mark as superseded`;
}

function ModalContent({
  onSupersedeCreateSuccess,
  currentCourse,
  onClose,
}: {
  onSupersedeCreateSuccess: () => void;
  currentCourse: PaginatedCourse;
  onClose: () => void;
}) {
  const { currentStep, goToNextStep, goToPrevStep, setStep } = useStep({
    maxStep: 1,
    initialStep: 0,
  });

  const { data: allCoursesData = [] } = trpcAdmin.courses.listCourses.useQuery({
    filters: {
      sort: SortOrder.ASC,
      orderBy: 'title',
      status: [CourseStatusSchema.enum.PUBLISHED],
    },
  });

  const { data: supersededCourse } = trpcAdmin.courses.courseSupersessionHistory.useQuery(
    currentCourse?.status === CourseStatusSchema.enum.SUPERSEDED ||
      currentCourse?.status === CourseStatusSchema.enum.PENDING_SUPERSESSION
      ? { courseId: currentCourse.id }
      : skipToken,
  );

  const allCourses = allCoursesData.filter((course) => course.id !== currentCourse.id);

  const [replacementCourse, setReplacementCourse] = useState<Course | undefined>(undefined);
  const [formData, setFormData] = useState<SupersedeFormData | undefined>(undefined);

  const toast = useToast();

  const effectiveFormData = useMemo(() => {
    if (formData) {
      return formData;
    }

    if (supersededCourse) {
      // Find the full superseding course object from allCourses
      const supersedingCourse = supersededCourse.supersededByCourse
        ? allCourses.find((c) => c.id === supersededCourse.supersededByCourse?.id)
        : undefined;

      const derived = {
        supersededEffectiveDate: supersededCourse.supersededEffectiveDate
          ? djs(supersededCourse.supersededEffectiveDate).format('YYYY-MM-DD')
          : '',
        teachOutDate: supersededCourse.teachOutEndDate
          ? djs(supersededCourse.teachOutEndDate).format('YYYY-MM-DD')
          : '',
        notes: supersededCourse.reason ?? '',
        supersededByCourse: supersedingCourse,
      };

      return derived;
    }
    return undefined;
  }, [formData, supersededCourse, allCourses]);

  const resetAndClose = () => {
    setReplacementCourse(undefined);
    setFormData(undefined);
    setStep(0);
    onClose();
  };

  const markAsPendingSupersessionMutation = trpcAdmin.courses.markAsPendingSupersession.useMutation(
    {
      onSuccess() {
        toast({
          status: 'success',
          title: t`Course marked as superseded successfully`,
        });
        onSupersedeCreateSuccess();
        resetAndClose();
      },
      onError(error) {
        toast({
          status: 'error',
          title: error?.message ?? t`An error occurred while superseding the course.`,
        });
      },
    },
  );

  const handleSubmit = useCallback(() => {
    if (!replacementCourse || !effectiveFormData) {
      toast({
        status: 'error',
        title: t`Missing required information`,
      });
      return;
    }

    markAsPendingSupersessionMutation.mutate({
      courseId: currentCourse.id,
      supersededByCourseId: replacementCourse.id,
      teachOutEndDate: effectiveFormData.teachOutDate,
      supersededEffectiveDate: effectiveFormData.supersededEffectiveDate,
      notes: effectiveFormData.notes,
    });
  }, [
    currentCourse.id,
    replacementCourse,
    effectiveFormData,
    markAsPendingSupersessionMutation,
    toast,
  ]);

  const isEffectiveDatePassed = useMemo(() => {
    if (supersededCourse?.supersededEffectiveDate == null) {
      return false;
    }
    return djs(supersededCourse.supersededEffectiveDate).isBefore(djs().startOf('day'));
  }, [supersededCourse?.supersededEffectiveDate]);

  const isTeachOutDatePassed = useMemo(() => {
    if (supersededCourse?.teachOutEndDate == null) {
      return false;
    }
    return djs(supersededCourse.teachOutEndDate).isBefore(djs().startOf('day'));
  }, [supersededCourse?.teachOutEndDate]);

  const title = getSupersessionModalTitle(currentCourse.status);

  if (currentCourse.status === CourseStatusSchema.enum.SUPERSEDED) {
    return (
      <Box w="full" p={6}>
        <SupersessionSummary currentCourse={currentCourse} onClose={resetAndClose} title={title} />
      </Box>
    );
  }

  return (
    <Box w="full" p={6}>
      <CourseMarkSupersededSteps
        currentStep={currentStep}
        goToNextStep={goToNextStep}
        goToPrevStep={goToPrevStep}
        currentCourse={currentCourse}
        replacementCourse={replacementCourse}
        setReplacementCourse={setReplacementCourse}
        setFormData={setFormData}
        allCourses={allCourses}
        handleSubmit={handleSubmit}
        isSubmitting={markAsPendingSupersessionMutation.isPending}
        initialFormData={effectiveFormData}
        title={title}
        isEffectiveDatePassed={isEffectiveDatePassed}
        isTeachOutDatePassed={isTeachOutDatePassed}
      />
    </Box>
  );
}

export function CourseMarkSupersededModal({
  isOpen,
  onClose,
  onSupersedeCreateSuccess,
  currentCourse,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSupersedeCreateSuccess: () => void;
  currentCourse: PaginatedCourse;
}) {
  return (
    <FormModalWrapper
      modalOpenButtonText={getSupersessionModalTitle(currentCourse.status)}
      modalOpenButtonLeftIcon={PlusIcon}
      modalContent={
        <ModalContent
          onSupersedeCreateSuccess={onSupersedeCreateSuccess}
          currentCourse={currentCourse}
          onClose={onClose}
        />
      }
      isOpen={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      modalOpenButtonVariant="secondary"
      modalContentHeight="auto"
    />
  );
}
